import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useSearchParams } from 'react-router-dom';
import { MAX_REQUEST_LIMIT } from 'shared/hooks/paged-list/constants';
import { useLocalStorage } from 'shared/hooks/use-local-storage';

/**
 * URL-состояние страницы-категории: номер страницы, размер страницы и поиск.
 *
 * Источник правды — URL, поэтому ссылку `/teams?page=3&perPage=20&search=спартак`
 * можно отправить коллеге и он увидит тот же экран. Единственное исключение —
 * `perPage` дублируется в localStorage: это настройка рабочего места, и при
 * следующем заходе на чистый `/teams` она должна подхватиться.
 *
 * Хук намеренно написан поверх `useSearchParams`, а не поверх
 * `shared/hooks/use-query-params`: последний умеет удалять параметр только по
 * `undefined`, поэтому дефолтные значения оседали бы в URL, и не даёт доступа к
 * готовой строке запроса, без которой не построить `href` для пагинатора.
 */

/**
 * Верхняя граница `perPage` — потолок самого бэкенда. Больше просить нельзя: он
 * молча вернёт двадцать, а страница ушла бы вперёд на `perPage`, и разница между
 * двадцаткой и размером страницы потерялась бы между страницами.
 */
const MAX_PER_PAGE = MAX_REQUEST_LIMIT;

const DEFAULTS = {
  perPage: 10,
  minSearchLength: 0,
  searchDebounceMs: 400,
};

export interface ListUrlStateOptions {
  /**
   * Ключ для запоминания `perPage` между визитами. В localStorage кладётся как
   * `${storageKey}PerPage`. Без ключа `perPage` живёт только в URL.
   */
  storageKey?: string;
  /** Сколько элементов на странице по умолчанию. */
  defaultPerPage?: number;
  /**
   * Ввод короче этой длины в запрос не уходит (`search` наружу — пустая строка).
   * Поле ввода при этом остаётся заполненным, а индикатор загрузки не зажигается.
   */
  minSearchLength?: number;
  /** Задержка между вводом и записью поиска в URL. */
  searchDebounceMs?: number;
}

export interface ListUrlState {
  /** Текущая страница, 1-based. */
  page: number;
  /** Текущий размер страницы. */
  perPage: number;
  /** Поисковый запрос, который надо отправлять на сервер (учитывает `minSearchLength`). */
  search: string;
  /** То, что набрано в поле ввода прямо сейчас — ещё до дебаунса. */
  draftSearch: string;
  /** Введённое отличается от отправленного: запрос вот-вот полетит. */
  isSearchPending: boolean;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setSearch: (value: string) => void;
  /** Адрес этой же страницы списка с другим номером страницы — для `<a href>`. */
  buildPageHref: (page: number) => string;
}

/** `?page=abc`, `?page=0`, `?page=-3` — всё это первая страница. */
function parsePage(raw: string | null): number {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.floor(parsed));
}

function parsePerPage(raw: string | null, fallback: number): number {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return Math.min(MAX_PER_PAGE, Math.floor(parsed));
}

/** Пустая строка и `undefined` означают «параметра в URL нет». */
function writeParam(params: URLSearchParams, key: string, value: string | undefined): void {
  if (value === undefined || value === '') {
    params.delete(key);
  } else {
    params.set(key, value);
  }
}

export function useListUrlState(options: ListUrlStateOptions = {}): ListUrlState {
  const {
    storageKey,
    defaultPerPage = DEFAULTS.perPage,
    minSearchLength = DEFAULTS.minSearchLength,
    searchDebounceMs = DEFAULTS.searchDebounceMs,
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();

  // ---- perPage: URL важнее localStorage, localStorage важнее дефолта --------

  const storageConfig = useMemo(
    () => ({
      perPage: {
        key: storageKey ? `${storageKey}PerPage` : 'perPage',
        defaultValue: defaultPerPage,
        parser: Number,
      },
    }),
    [storageKey, defaultPerPage],
  );

  const [storage, setStorage] = useLocalStorage(storageConfig);
  const storedPerPage = storageKey ? parsePerPage(String(storage.perPage), defaultPerPage) : defaultPerPage;

  const page = parsePage(searchParams.get('page'));
  const perPage = parsePerPage(searchParams.get('perPage'), storedPerPage);
  const urlSearch = searchParams.get('search') ?? '';

  // ---- Поиск: черновик в стейте, закоммиченное значение в URL ---------------

  const [draftSearch, setDraftSearch] = useState(urlSearch);
  /**
   * Последнее значение, которое в URL записали мы сами. Всё, что разошлось с
   * ним, пришло извне — переход «назад», клик по ссылке — и черновик обязан
   * подстроиться под URL, а не перезаписать его обратно.
   */
  const lastCommittedRef = useRef(urlSearch);

  useEffect(() => {
    if (urlSearch !== lastCommittedRef.current) {
      lastCommittedRef.current = urlSearch;
      setDraftSearch(urlSearch);
    }
  }, [urlSearch]);

  const toEffectiveSearch = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      return trimmed.length >= minSearchLength ? trimmed : '';
    },
    [minSearchLength],
  );

  const search = toEffectiveSearch(urlSearch);
  const isSearchPending = toEffectiveSearch(draftSearch) !== search;

  const commitSearch = useCallback(
    (value: string) => {
      lastCommittedRef.current = value;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          writeParam(next, 'search', value);
          // Найденное короче исходного списка — на седьмой странице результата
          // почти наверняка нет, поэтому поиск всегда возвращает на первую.
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (draftSearch === lastCommittedRef.current) return;

    const timer = setTimeout(() => commitSearch(draftSearch), searchDebounceMs);
    return () => clearTimeout(timer);
  }, [draftSearch, searchDebounceMs, commitSearch]);

  // ---- Колбэки --------------------------------------------------------------

  const setPage = useCallback(
    (nextPage: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        writeParam(next, 'page', nextPage > 1 ? String(nextPage) : undefined);
        return next;
      });
    },
    [setSearchParams],
  );

  const setPerPage = useCallback(
    (nextPerPage: number) => {
      const normalized = parsePerPage(String(nextPerPage), defaultPerPage);

      if (storageKey) {
        setStorage({ perPage: normalized });
      }

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // Пишем всегда, даже если совпало с дефолтом: иначе ссылка на список
          // открылась бы у другого человека с его собственным размером страницы.
          next.set('perPage', String(normalized));
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [defaultPerPage, setSearchParams, setStorage, storageKey],
  );

  const setSearch = useCallback((value: string) => {
    setDraftSearch(value);
  }, []);

  const buildPageHref = useCallback(
    (targetPage: number) => {
      const next = new URLSearchParams(searchParams);
      writeParam(next, 'page', targetPage > 1 ? String(targetPage) : undefined);
      const query = next.toString();
      return query ? `${pathname}?${query}` : pathname;
    },
    [pathname, searchParams],
  );

  return {
    page,
    perPage,
    search,
    draftSearch,
    isSearchPending,
    setPage,
    setPerPage,
    setSearch,
    buildPageHref,
  };
}
